import React, { useEffect, useMemo, useState} from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { showToast } from '../toasts';
import DynamicSelect from '../dynamic_select/DynamicSelect';
import ConfirmationPopup from '../team_manager/ClientBookerComponents/ConfirmationPopup';

const BlockedDriversTable = ({
    selectedCompany,
    selectedDriver,
    setSelectedDriver,
    selectedReason,
    setSelectedReason,
    resetSelection,
    refreshKey,
    onNewRowsChange,
    newRows = [],
    fleetOptions = [],
}) => {

    const [allDrivers, setAllDrivers] = useState([]);
    const [blockedDrivers, setBlockedDrivers] = useState([]);
    const [reasons, setReason] = useState([]);
    const [searchDriver, setSearchDriver] = useState("");
    const [localResetKey, setLocalResetKey] = useState(0);
    const [sortConfig, setSortConfig] = useState({ column: null, order: 'desc' });
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [driverToReactivate, setDriverToReactivate] = useState(null);
    // Loading state is local to this table so refreshing the blocked-drivers
    // list never makes the unrelated Assigned Fleets section flash a skeleton.
    // Starts true because the list is always fetched on mount, so the table
    // shows a loading row immediately instead of a brief "no drivers" flash.
    const [isTableLoading, setIsTableLoading] = useState(true);
    // Tracks the in-flight reactivation so the confirmation modal can show a
    // spinner and block double-submits.
    const [isReactivating, setIsReactivating] = useState(false);
    // Tracks loading of the available-drivers list so the dropdown shows a
    // "Loading..." indicator instead of "No options". Starts true because the
    // list is fetched on mount, avoiding a "No options" flash before the fetch.
    const [isDriversLoading, setIsDriversLoading] = useState(true);

    // Map enum keys to display values
    const getReasonDisplayValue = (reasonKey) => {
        const reasonMap = {
            'sexual_harassment_pending_an_investigation': 'Sexual harassment pending an investigation',
            'drunk_driving_pending_an_investigation': 'Drunk driving pending an investigation',
            'speeding_pending_an_investigation': 'Speeding pending an investigation',
            'reckless_driving': 'Reckless driving',
            'improper_conduct': 'Improper conduct',
            'poor_vehicle_condition': 'Poor vehicle condition',
            'other': 'Other'
        };
        return reasonMap[reasonKey] || reasonKey;
    };

    // Map fleet id to name
    const fleetIdToName = useMemo(() => {
        const map = new Map();
        fleetOptions.forEach(opt => map.set(opt.value, opt.label));
        return map;
    }, [fleetOptions]);

    // Sort values according to sortConfig
    const sortedBlockedDrivers = useMemo(() => {
        if (!sortConfig.column) return blockedDrivers;

        return [...blockedDrivers].sort((a, b) => {
            let aValue, bValue;

            switch (sortConfig.column) {
                case 'name':
                    // Compare driver names
                    aValue = `${a.user.name} ${a.user.surname}`.toLowerCase();
                    bValue = `${b.user.name} ${b.user.surname}`.toLowerCase();
                    break;
                case 'fleet':
                    aValue = fleetIdToName.get(a.user?.fleet_id) || '';
                    bValue = fleetIdToName.get(b.user?.fleet_id) || '';
                    break;
                case 'blocked_by':
                    aValue = a.deactivator_name || '';
                    bValue = b.deactivator_name || '';
                    break;
                case 'blocked_date':
                    // Compare date objects
                    aValue = new Date(a.created_at);
                    bValue = new Date(b.created_at);
                    break;
                case 'reason':
                    // Compare reason strings
                    aValue = getReasonDisplayValue(a.reason);
                    bValue = getReasonDisplayValue(b.reason);
                    break;
                default:
                    return 0;
            }

            // Apply ascending/descending order based on comparisons
            if (aValue < bValue) return sortConfig.order === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [blockedDrivers, sortConfig, fleetOptions]);
    
    const filteredBlockedDrivers = sortedBlockedDrivers;

    // Format date to dd/mm/yyyy
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    // Fetch the blocked-drivers list shown in the table. Respects the search
    // term and drives the table's own loading state only.
    const fetchDeactivations = async () => {
        try {
            setIsTableLoading(true);

            const response = await axiosInstance.get('/user_company_deactivations.json', {
                params: {
                    company_id: selectedCompany.id,
                    search: searchDriver.trim() || undefined
                }
            });

            const activeDeactivations = response.data.deactivations?.filter(
                deactivation => !deactivation.reactivated_at
            ) || [];

            setBlockedDrivers(activeDeactivations);
            setReason(response.data.reasons || []);
        } catch (error) {
            console.error('Error fetching blocked drivers:', error);
            showToast('error', 'Could not load blocked drivers.');
            setBlockedDrivers([]);
        } finally {
            setIsTableLoading(false);
        }
    };

    // Fetch the drivers available to deactivate (the dropdown options). The new
    // action returns every driver the current user can see, scoped by role and
    // already excluding drivers with an active deactivation.
    const fetchAvailableDrivers = async () => {
        try {
            setIsDriversLoading(true);

            const response = await axiosInstance.get('/user_company_deactivations/new.json', {
                params: {
                    company_id: selectedCompany.id,
                }
            });

            setAllDrivers(response.data || []);
        } catch (error) {
            console.error('Error fetching available drivers:', error);
            setAllDrivers([]);
        } finally {
            setIsDriversLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedCompany?.id) {
            // Nothing to fetch without a company; clear the initial loading state.
            setIsTableLoading(false);
            setIsDriversLoading(false);
            return;
        }
        fetchDeactivations();
        fetchAvailableDrivers();
        onNewRowsChange([]);
    }, [selectedCompany?.id, refreshKey]);

    // Searching only filters the table; the dropdown options do not change, so
    // there is no need to reload the available-drivers list here.
    const handleSearch = () => {
        fetchDeactivations();
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Handle column sorting
    const handleSort = (column) => {
        setSortConfig(prevConfig => {
            if (prevConfig.column === column) {
                return {
                    column: column,
                    order: prevConfig.order === 'asc' ? 'desc' : 'asc'
                };
            } else {
                return {
                    column: column,
                    order: 'desc'
                };
            }
        });
    };

    // Add new deactivation row
    const handleAddNewRow = () => {
        if (!selectedDriver || !selectedReason) {
            return;
        }
        const driver = allDrivers.find(d => d.id === selectedDriver);
        if (!driver) {
            return;
        }
        const newRow = {
            id: `temp-${Date.now()}`,
            user_id: driver.id,
            user: {
                name: driver.name,
                surname: driver.surname,
                fleet_id: driver.fleet_id || driver.fleetId || driver?.fleet?.id,
                deactivated_by: "",
            },
            created_at: "",
            reason: selectedReason,
            _isNewRow: true,
        };
        onNewRowsChange([newRow, ...(newRows || [])]);
        setSelectedDriver(null);
        setSelectedReason(null);
        setLocalResetKey(prev => prev + 1);
    };

    const removeNewRow = (tempId) => {
        onNewRowsChange((newRows || []).filter(r => r.id !== tempId));
    };

    // Reactivate driver
    const reactivateDriver = (deactivationId) => {
        setDriverToReactivate(deactivationId);
        setShowConfirmPopup(true);
    }

    // Handle confirmation of reactivation
    const handleConfirmReactivation = async () => {
        const deactivationId = driverToReactivate;

        setIsReactivating(true);
        try {
            const response = await axiosInstance.patch(
                `/user_company_deactivations/${deactivationId}/reactivate_user.json`,
                { company_id: selectedCompany.id }
            );

            if (!response.data.success) {
                throw new Error(response.data.error || 'Reactivation failed');
            }

            // Remove the row and close the modal immediately rather than waiting
            // on a full refetch, then reconcile the dropdown options in the
            // background (the driver is now selectable again).
            setBlockedDrivers(current => current.filter(deactivation => deactivation.id !== deactivationId));
            setShowConfirmPopup(false);
            setDriverToReactivate(null);
            showToast('success', 'Driver reactivated.');
            fetchAvailableDrivers();
        } catch (error) {
            console.error('Error reactivating the driver:', error);
            // Leave the modal open so the user can retry or cancel.
            showToast('error', 'Could not reactivate the driver. Please try again.');
        } finally {
            setIsReactivating(false);
        }
    }

    // Handle cancellation of reactivation
    const handleCancelReactivation = () => {
        if (isReactivating) return;
        setShowConfirmPopup(false);
        setDriverToReactivate(null);
    }

    return (
        <div className="flex flex-wrap">
            <div className="w-full flex items-end">
                {/* Driver selection */}
                <div className="w-full md:w-1/2 flex flex-col pr-1">
                    <label className="label font-medium text-gray-900">Select Driver</label>
                    <DynamicSelect
                        key={`driver-select-${resetSelection}-${localResetKey}`}
                        props={{
                            onChange: (selected) => {
                                setSelectedDriver(selected?.target.value?.value);
                            },
                            options: allDrivers
                                .filter(driver => {
                                    const isNewDriver = newRows.some(row => row.user_id === driver.id);
                                    const isSaved = blockedDrivers.some(deactivation => deactivation.user_id === driver.id);
                                    return !isNewDriver && !isSaved;
                                })
                                .map(driver => ({
                                    value: driver.id,
                                    label: `${driver.name} ${driver.surname}`
                                })),
                            placeholder: "Select Driver",
                            value: selectedDriver,
                            closeMenuOnSelect: true,
                            menuPortal: true,
                            isLoading: isDriversLoading
                        }}
                    />
                </div>

                {/* Reason selection */}
                <div className="w-full md:w-1/2 flex flex-col px-2">
                    <label className="label font-medium text-gray-900">Select Reason</label>
                    <DynamicSelect
                        key={`reason-select-${resetSelection}-${localResetKey}`}
                        props={{
                            onChange: (selected) => {
                                setSelectedReason(selected?.target.value?.value);
                            },
                            options: reasons.map(reason => ({
                                value: reason,
                                label: reason
                            })),
                            placeholder: "Select Reason",
                            value: selectedReason,
                            closeMenuOnSelect: true,
                            menuPortal: true
                        }}
                    />
                </div>
                <button
                    className={`button rounded flex items-center justify-center relative cursor-pointer`}
                    style={{ width: "48px", height: "48px", marginRight: "15px" }}
                    onClick={handleAddNewRow}
                    disabled={!selectedDriver || !selectedReason}
                >
                    <i className="fas fa-plus" style={{ fontSize: '24px' }} />
                </button>
            </div>

            {/* Search bar */}
            <div className="flex w-1/3 gap-2 mt-8">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search blocked drivers..."
                        className="w-full pr-12 pl-4 py-3 mt-1 border rounded-md text-sm"
                        value={searchDriver}
                        onChange={(e) => setSearchDriver(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                    />
                    
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pt-2 pointer-events-none">
                        <i className="fas fa-search text-gray-400 text-sm" />
                    </div>
                </div>
            </div>

            <div className="w-full mt-3 mb-2 max-h-96 overflow-y-auto overflow-x-auto">
                <table className="table-auto w-full">
                    <thead>
                        <tr>
                            <th 
                                className="text-left px-2 py-2 align-left text-sm cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleSort('name')}
                            >
                                <span className="inline-flex items-center">
                                    Driver Name & Surname
                                    <i className="fas fa-sort ml-1 text-gray-400"></i>
                                </span>
                            </th>
                            <th 
                                className="text-left px-2 py-2 align-left text-sm cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleSort('fleet')}
                            >
                                <span className="inline-flex items-center">
                                    Fleet
                                    <i className="fas fa-sort ml-1 text-gray-400"></i>
                                </span>
                            </th>
                            <th 
                                className="text-left px-2 py-2 align-left text-sm cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleSort('blocked_by')}
                            >
                                <span className="inline-flex items-center">
                                    Blocked By
                                    <i className="fas fa-sort ml-1 text-gray-400"></i>
                                </span>
                            </th>
                            <th 
                                className="text-left px-2 py-2 align-left text-sm cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleSort('blocked_date')}
                            >
                                <span className="inline-flex items-center">
                                    Blocked Date
                                    <i className="fas fa-sort ml-1 text-gray-400"></i>
                                </span>
                            </th>
                            <th 
                                className="text-left px-2 py-2 align-left text-sm cursor-pointer hover:bg-gray-50 select-none"
                                onClick={() => handleSort('reason')}
                            >
                                <span className="inline-flex items-center">
                                    Reason
                                    <i className="fas fa-sort ml-1 text-gray-400"></i>
                                </span>
                            </th>
                            <th className="px-2 py-2 text-sm">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {newRows.map((newRow) => (
                            <tr key={newRow.id} className="bg-yellow-50">
                                <td className="px-4 py-2 text-sm">{`${newRow.user.name} ${newRow.user.surname}`}</td>
                                <td className="px-2 py-2 text-sm text-gray-800">{fleetIdToName.get(newRow.user?.fleet_id) || 'N/A'}</td>
                                <td className="px-2 py-2 text-sm text-gray-800">{newRow.user?.deactivated_by}</td>
                                <td className="px-2 py-2 text-sm text-gray-800">{formatDate(newRow.created_at)}</td>
                                <td className="px-2 py-2 text-sm text-gray-800">{getReasonDisplayValue(newRow.reason)}</td>
                                <td className="px-2 py-2 text-sm">
                                    <button
                                        type="button"
                                        className="bg-gray-400 text-white rounded font-bold text-xl items-center justify-center relative cursor-pointer"
                                        style={{ width: "40px", height: "45px" }}
                                        onClick={() => removeNewRow(newRow.id)}
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredBlockedDrivers.map((deactivation) => (
                            <tr key={deactivation.id}>
                                <td className="px-4 py-2 border-b text-sm">
                                    {`${deactivation.user.name} ${deactivation.user.surname}`}
                                </td>
                                <td className="px-2 py-2 border-b text-sm text-gray-800">{fleetIdToName.get(deactivation.user?.fleet_id) || ''}</td>
                                <td className="px-2 py-2 border-b text-sm text-gray-800">{deactivation.deactivator_name || ''}</td>
                                <td className="px-2 py-2 border-b text-sm text-gray-800">{formatDate(deactivation.created_at)}</td>
                                <td className="px-2 py-2 border-b text-sm text-gray-800">{getReasonDisplayValue(deactivation.reason)}</td>
                                <td className="px-2 py-2 border-b text-sm">
                                    <button
                                        type="button"
                                        className="bg-red-600 text-white rounded font-bold text-sm items-center justify-center relative cursor-pointer"
                                        style={{ width: "40px", height: "45px" }}
                                        onClick={()=> reactivateDriver(deactivation.id)}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {isTableLoading && (
                            <tr>
                                <td colSpan="6" className="px-4 py-4 text-center text-gray-500">
                                    <i className="fas fa-spinner fa-spin mr-2" />
                                    Loading...
                                </td>
                            </tr>
                        )}
                        {!isTableLoading && filteredBlockedDrivers.length === 0 && newRows.length === 0 && selectedCompany?.id && (
                            <tr>
                                <td colSpan="6" className="px-4 py-4 text-center text-gray-500">
                                    {searchDriver.trim() ? 'No deactivated drivers found matching your search' : 'No deactivated drivers found for this company'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirmation popup for reactivating drivers */}
            {showConfirmPopup && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={(e) => { if (e.target === e.currentTarget) handleCancelReactivation(); }}
                >
                    <ConfirmationPopup
                        title="Reactivate Driver"
                        body="Are you sure you want to reactivate this driver for this company?"
                        onNoClick={handleCancelReactivation}
                        onYesClick={handleConfirmReactivation}
                        warning={true}
                        loading={isReactivating}
                    />
                </div>
            )}
        </div>
    );
};

export default BlockedDriversTable;